import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { Button } from "@mui/material";
import { Save } from "@mui/icons-material";
import Axios from "../axios";
import { toast } from "react-toastify";
import { useAuth } from "../auth/authContext";
import { useLocation } from "react-router-dom";
import { useClientReactQuill } from "../utils/reactQuillLoader";
import "react-quill/dist/quill.snow.css";

const extractImageUrls = (html) => {
  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  const urls = new Set();
  let match;
  while ((match = imgRegex.exec(html))) {
    urls.add(match[1]);
  }
  return urls;
};

const handleImageUpload = async (file) => {
  try {
    const formData = new FormData();
    formData.append("image", file);

    const res = await Axios.post(
      process.env.REACT_APP_BACKEND + "aboutus-upload-image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return res.data.fileUrl;
  } catch (error) {
    console.error("Upload error", error);
    toast.error("Image upload failed");
    return "";
  }
};

const handleImageDelete = async (fileUrl) => {
  try {
    await Axios.post(process.env.REACT_APP_BACKEND + "aboutus-delete-image", {
      fileUrl,
    });
    console.log("Deleted:", fileUrl);
  } catch (error) {
    console.error("Image deletion failed", error);
  }
};

const modules = {
  toolbar: {
    container: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
    handlers: {
      image: function () {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            const imageUrl = await handleImageUpload(file);
            if (imageUrl) {
              const editor = this.quill;
              const range = editor.getSelection(true);
              editor.insertEmbed(range.index, "image", imageUrl);
            }
          }
        };
      },
    },
  },
  imageResize: {
    modules: ["Resize", "DisplaySize"],
  },
};

const AboutUs = () => {
  const ReactQuill = useClientReactQuill({ enableImageResize: true });
  const { navData } = useAuth();
  const location = useLocation();
  const [htmlContent, setHtmlContent] = useState("<p></p>");
  const [isLoading, setIsLoading] = useState(false);
  const prevImages = useRef(new Set());
  const write = Boolean(
    navData.find((item) => item.link === location.pathname)?.write
  );

  useEffect(() => {
    Axios.get(process.env.REACT_APP_BACKEND + "aboutus-get")
      .then((res) => {
        const initialHtml = res?.data?.html || "<p></p>";
        setHtmlContent(initialHtml);
        prevImages.current = extractImageUrls(initialHtml);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Failed to load About Us content"
        );
        setHtmlContent("<p></p>");
      });
  }, []);

  const handleSubmit = async () => {
    if (!write) {
      toast.error("You do not have permission to save content");
      return;
    }

    const currentImages = extractImageUrls(htmlContent);
    const deletedImages = [...prevImages.current].filter(
      (url) => !currentImages.has(url)
    );

    setIsLoading(true);

    try {
      for (const url of deletedImages) {
        await handleImageDelete(url);
      }

      await Axios.post(process.env.REACT_APP_BACKEND + "aboutus-save", {
        html: htmlContent,
      });

      toast.success("About Us content saved successfully");
      prevImages.current = currentImages;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save content");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sevenly-About</title>
        <meta name="Trip List" content="Trip List for Home Page" />
      </Helmet>
      <div className="p-4 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          Manage About Us Page
        </h2>
        <div className="h-96 mb-4 bg-white shadow-md rounded">
          {ReactQuill ? (
            <ReactQuill
              key="quill-editor"
              theme="snow"
              value={htmlContent}
              onChange={setHtmlContent}
              modules={modules}
              className="h-80"
              readOnly={!write}
            />
          ) : (
            <div className="h-80 p-3">{null}</div>
          )}
        </div>
        {write && (
          <Button
            className="w7 sevenly-btn text-white "
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={<Save />}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        )}
      </div>
    </>
  );
};

export default AboutUs;
